import { useRef, useState } from 'react';
import {
  fetchBookings,
  persistBookings as persistBookingsApi,
  bookingsAreServerAuthoritative,
  deleteBookingsByIds,
  BookingDeleteRefusedError,
} from '../lib/api/bookings';
import { errMsg } from '../lib/errors';

// ─────────────────────────────────────────────────────────────────────
// useBookings — the court-booking slice, lifted out of the Padel24AppMain
// god component (see docs/history/DECOMPOSITION_PLAN.md §4/§5, PR-F/PR-G; HANDOVER §13/R5).
//
// Owns ONE persisted slice + its synchronous mirror:
//   • bookings     (useState, Storage key 'bookings_v1', TENANT-SCOPED) — court
//                  bookings; the substrate Activities + Series build on.
//   • bookingsRef  (useRef, the R5 sync mirror) — a SYNCHRONOUS copy of `bookings`
//                  read by validation during rapid adds. The demo generator fires
//                  30+ addBooking calls before React re-renders, so closures over
//                  `bookings` are stale; every writer reads `bookingsRef.current`
//                  and updates it INLINE alongside setBookings. There is NO
//                  §13.20 blanket-persist effect for bookings (the ref-mirror is
//                  the mechanism, not a persist flag), so the hook must not touch
//                  Storage on mount.
//
// CRITICAL R5 (HANDOVER §13): EVERY setBookings MUST be paired with
// `bookingsRef.current = <sameNewList>` so the ref never lags the state. The hook
// owns the ref and EXPOSES it; the loader-bridge and all App-side booking mutators
// MUST keep updating bookingsRef.current in lockstep with setBookings.
//
// LOADER BRIDGE: the INITIAL load stays in the App-side atomic mount loader
// (one big useEffect that reads ~13 domains in one pass — DECOMPOSITION_PLAN
// principle #1 / R1). It flushes via `setBookings(list)` + `bookingsRef.current =
// list` (both exposed below). Do NOT move the load into this hook.
//
// SCOPE (conservative, correctness-first minimal lift): this hook owns the slice,
// the ref, and the four ENGINE-FREE, SINGLE-DOMAIN mutators copied VERBATIM from
// the former monolith:
//   • persistBookings      — Storage-first write helper ('bookings_v1').
//   • deleteBooking        — drop one id; ref + setState in lockstep.
//   • deleteManyBookings   — batch drop; reads FRESH from Storage first (so it
//                            never trusts a stale ref that missed panel edits).
//   • syncBookingsFromPanel — mirror a list the booking panel wrote directly.
//
// STAYS IN APP (engine + cross-domain coupling — uses the exposed setBookings +
// bookingsRef): addBooking / addManyBookings / updateBooking / replaceSeries.
// They call the runtime Padel24Engine.booking.createBooking/validateBooking AND
// read App-side domains (`bookingConfig`, `clubInfo?.courts`). Although
// createBooking/validateBooking themselves now live in src/engine/booking.ts, the
// mutators reach them via the runtime `Padel24Engine` object assembled in App
// and depend on App-side config/club state — so per the
// "when in doubt KEEP IT IN APP" rule they stay, consuming this hook's setters.
// The activities-coupled flows (openActivityReservation / closeActivityReservation
// / auto-cancel / topUpRollingSeries / replaceSeries) also stay in App; the ones
// that delete bookings call the exposed deleteManyBookings.
// ─────────────────────────────────────────────────────────────────────

/** A court booking (loosely typed — the engine treats bookings as `any`). */
export type Booking = any;

/** Result shape shared by deleteBooking (mirrors the former monolith's returns). */
export interface BookingMutationResult {
  success: boolean;
  error?: string;
  /**
   * The server's refusal CODE where the screen has a sentence for it — today
   * only `booking_has_payment` (#284), which points staff to «Avbestill og
   * refunder». Never translated here.
   */
  code?: string;
}

/** Result shape for the batch delete (mirrors the former monolith exactly). */
export interface BookingBatchDeleteResult {
  success: boolean;
  error?: string;
  /** As on `BookingMutationResult`. */
  code?: string;
  removed: number;
}

/** The refusal code a delete failure carries, if the screen has a sentence for it. */
const refusalCode = (e: unknown): { code?: string } =>
  e instanceof BookingDeleteRefusedError ? { code: e.code } : {};

export function useBookings() {
  // Court bookings — tenant-scoped in 'bookings_v1'.
  const [bookings, setBookings] = useState<Booking[]>([]);
  // useRef mirrors the bookings state synchronously so that addBooking can validate
  // against the latest value even on rapid successive calls (the demo generator
  // fires 30+ calls before React has had a chance to re-render).
  const bookingsRef = useRef<Booking[]>([]);

  // ──── BOOKING CRUD ────
  // Storage-first: write to 'bookings_v1' BEFORE setState. (Verbatim.)
  const persistBookings = async (list: Booking[]) => {
    try {
      await persistBookingsApi(list);
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre bookinger:', e);
      throw e;
    }
  };

  const deleteBooking = async (bookingId: string): Promise<BookingMutationResult> => {
    const baseList = bookingsRef.current || bookings;
    const newList = baseList.filter((b) => b.id !== bookingId);
    if (newList.length === baseList.length) return { success: false, error: 'Booking ikke funnet' };
    try {
      // #245: delete the row named, rather than writing back a list that is
      // missing it — which also deleted anything created since the read.
      if (bookingsAreServerAuthoritative()) await deleteBookingsByIds([String(bookingId)]);
      else await persistBookings(newList);
      bookingsRef.current = newList;
      setBookings(newList);
      return { success: true };
    } catch (e) {
      return { success: false, error: errMsg(e), ...refusalCode(e) };
    }
  };

  // Batch delete: remove many bookings with ONE Storage write + one re-render.
  const deleteManyBookings = async (ids: string[]): Promise<BookingBatchDeleteResult> => {
    // Read fresh from Storage so we always delete from the calendar's ACTUAL state
    // (not a stale bookingsRef that may be missing recent panel edits).
    let baseList: Booking[];
    try {
      baseList = await fetchBookings();
    } catch {
      baseList = bookingsRef.current || bookings;
    }
    const del = new Set(ids);
    const newList = baseList.filter((b) => !del.has(b.id));
    const removed = baseList.length - newList.length;
    try {
      // #245. Note this handler already re-read the calendar before filtering —
      // a hand-rolled narrowing of the staleness window. Naming the rows makes
      // the re-read unnecessary rather than merely shorter.
      if (bookingsAreServerAuthoritative()) await deleteBookingsByIds(ids.map(String));
      else await persistBookings(newList);
      bookingsRef.current = newList;
      setBookings(newList);
      return { success: true, removed };
    } catch (e) {
      return { success: false, error: errMsg(e), ...refusalCode(e), removed: 0 };
    }
  };

  // The panel writes bookings_v1 directly — mirror the change into app state.
  const syncBookingsFromPanel = (list: Booking[]) => {
    const arr = Array.isArray(list) ? list : [];
    bookingsRef.current = arr;
    setBookings(arr);
  };

  /**
   * Re-read the calendar from the server (#245).
   *
   * For a command that deleted bookings SERVER-side — activity cancellation
   * frees the courts inside its own transaction — so there is nothing to
   * compute here, only a newer answer to adopt. R5: the ref moves in lockstep
   * with the state, as every other writer in this hook does.
   */
  const refreshBookings = async () => {
    const list = await fetchBookings();
    bookingsRef.current = list;
    setBookings(list);
  };

  return {
    bookings,
    // The R5 sync mirror — owned here, consumed by App-side booking mutators
    // (addBooking/addManyBookings/updateBooking/replaceSeries) and the
    // activities flows that read bookingsRef.current.
    bookingsRef,
    // Exposed for the App-side atomic mount loader (initial flush:
    // setBookings(list) + bookingsRef.current = list) and for the engine/
    // cross-domain booking mutators that live in App.
    setBookings,
    persistBookings,
    deleteBooking,
    deleteManyBookings,
    refreshBookings,
    syncBookingsFromPanel,
  };
}
